/** @jsxRuntime automatic */
/** @jsxImportSource preact */
// OWNER: FRONTEND-DIALER. CONTRACT: component name and props fixed (LEADGEN-SPEC §11). Used by the dialer (I toggles,
// Esc closes, R re-runs the live check while open; non-modal) and by LG-ADMIN's Results tab with readOnly=true
// (no Verify, no live check, no Research; data from leadgenApi.placeDrawer). Renders only from props: no fetching here.
//
// Layout (signals-scoring §7.2): header (who, open now) → the hook (Verify, don't-say, backup) → two columns (map and
// phone mentions | website, people, our history) → footer (why this tier) → below the fold. It is an <aside>, never a
// role="dialog": the dialer's hotkeys stay live while it is open. The pragmas above let the unit tests render DrawerView.
import './drawer.css';
import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { DrawerData, DrawerHook, LiveCheck } from '../../../../src/shared/api-leadgen';
import { recycleRoundLabel, recycleRoundTitle } from '../../../../src/shared/api';
import { DISPOSITION_META } from '../labels';
import { localClock } from '../time';
import { areaSearchQuery, areaSearchUrl, FIND_ON_MAPS_TITLE, googleProfileSlot, NO_PROFILE_TITLE, NO_WEBSITE_TITLE } from '../links';
import {
  categoryLine, countLine, daysAgo, dontSayLine, fmtClock, fmtMonth, fmtShortDate, historyLine, hookVerified, hookView, hostOf, hoursLineFor,
  isWebLink, lcpLine, mentionMeta, OFFER_LABEL, openLine, paceLine, rankLine, ratingLine, reasonsLine, replyRateLine, reviewsMeasured,
  researchRunning, scoreLine, shownMentions, siteLoadsLine, usdLabel, USAGE_LABEL, verifyGate, yesNo,
} from './view';

export interface BusinessDrawerProps {
  data: DrawerData | null;
  live: LiveCheck | null;                          // page memory only; dropped when the lock releases or the next lead loads
  loading: boolean;
  error: string | null;
  readOnly: boolean;
  pinned?: boolean;                                // ≥ 1600 px: third column instead of an overlay
  onClose: () => void;
  onRecheck?: () => void;                          // R
  onVerify?: (hook: DrawerHook) => void;           // POST /api/businesses/:id/verify-observation (a click, never a key)
  onResearch?: () => void;                         // POST /api/leadgen/research/:businessId (about $0.03)
}

/** Re-render every 15 s for the prospect's clock and "open now". */
function useClock(ms: number): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function BusinessDrawer(props: BusinessDrawerProps) {
  const now = useClock(15_000);
  // Evidence links opened for this lead: Verify on an unchecked hook needs its evidence opened first.
  const [opened, setOpened] = useState<string[]>([]);
  const key = props.data ? `${props.data.businessId ?? ''}:${props.data.leadPlaceId ?? ''}` : '';
  useEffect(() => { setOpened([]); }, [key]);
  return (
    <DrawerView {...props} now={new Date(now)} evidenceOpened={opened}
      onEvidenceOpened={(url) => setOpened((o) => (o.includes(url) ? o : [...o, url]))} />
  );
}

export interface DrawerViewProps extends BusinessDrawerProps {
  now: Date;
  evidenceOpened: string[];
  onEvidenceOpened: (url: string) => void;
}

/** Link out (http/https only; anything else is shown as text), new tab, no referrer. */
function Ext(props: { href: string | null | undefined; children: ComponentChildren; class?: string; title?: string; onOpen?: () => void }) {
  if (!props.href) return null;
  if (!isWebLink(props.href)) return <span class={`muted ${props.class ?? ''}`} title="Not a web link">{props.children}</span>;
  return (
    <a href={props.href} target="_blank" rel="noopener noreferrer" class={props.class} title={props.title}
      onClick={(e) => { props.onOpen?.(); (e.currentTarget as HTMLElement).blur(); }}>
      {props.children}
    </a>
  );
}

/** Google requires attribution next to Places content (LEADGEN-SPEC §8.8). */
function Attr() {
  return <span class="vd-bd-attr" title="Live data from Google Maps">Google Maps</span>;
}

function blurThen(fn: (() => void) | undefined) {
  return (e: Event) => { (e.currentTarget as HTMLElement | null)?.blur?.(); fn?.(); };
}

export function DrawerView(p: DrawerViewProps) {
  const cls = `vd-bd${p.pinned ? ' vd-bd-pinned' : ''}${p.readOnly ? ' vd-bd-ro' : ''}`;
  const close = (
    <button class="btn btn-sm" onClick={blurThen(p.onClose)} aria-label="Close business info" title="Close (Esc or I)">
      Close {p.readOnly ? null : <kbd>Esc</kbd>}
    </button>
  );
  if (!p.data) {
    return (
      <aside class={cls} aria-label="Business info">
        <div class="vd-bd-head"><div class="vd-bd-title"><b>Business info</b></div><div class="vd-bd-actions">{close}</div></div>
        <div class="vd-bd-scroll">
          {p.loading ? <p class="muted">Loading business info… The lead card and calling are not affected.</p>
            : p.error ? <p class="vd-bad">{p.error}{p.readOnly ? '' : ' Press I twice to try again.'}</p>
              : <p class="muted">No business info for this lead.</p>}
        </div>
      </aside>
    );
  }

  const d = p.data;
  const h = d.header;
  const live = p.live;
  const lp = live?.places && live.places.ok ? live.places.data : null;
  let clock: string | null = null;
  try { clock = localClock(p.now, h.tz); } catch { clock = null; }
  const open = openLine(d, live, p.now);
  const rating = ratingLine(d, live);
  const category = categoryLine(d);
  const mapsUrl = lp?.googleMapsUri ?? h.mapsUrl;
  const profile = googleProfileSlot(mapsUrl, { name: h.name, address: h.address ?? null, city: h.city, stateProv: h.stateProv, country: h.country });
  const area = { niche: h.niche ?? null, city: h.city, stateProv: h.stateProv, country: h.country };
  const areaUrl = areaSearchUrl(area);
  const checkingLive = p.loading && !p.readOnly;
  const researching = researchRunning(d);

  return (
    <aside class={cls} aria-label={`Business info: ${h.name}`}>
      <div class="vd-bd-head">
        <div class="vd-bd-title">
          <b class="vd-bd-name" title={h.name}>{h.name}</b>
          {h.tier ? <span class={`chip vd-bd-tier vd-bd-tier-${h.tier}`} title="Tier and priority for this campaign's offer">{h.tier}{h.priority !== null ? ` ${h.priority}` : ''}</span> : null}
        </div>
        <div class="vd-bd-actions">
          {/* PJ 2026-10-09: a muted tag where a missing link would be, the Google Maps search fallback, "Search the area". */}
          {profile.kind === 'profile' ? <Ext href={profile.href} class="btn btn-sm" title="Open their Google Maps page">Maps</Ext>
            : <span class="chip chip-muted" title={NO_PROFILE_TITLE}>No Google link on file</span>}
          {profile.kind === 'search' ? <Ext href={profile.href} class="btn btn-sm" title={FIND_ON_MAPS_TITLE}>Find on Google Maps</Ext> : null}
          {h.website ? <Ext href={h.website} class="btn btn-sm" title={h.website}>Website</Ext>
            : <span class="chip chip-muted" title={NO_WEBSITE_TITLE}>No website on file</span>}
          <Ext href={h.searchResultsUrl} class="btn btn-sm" title="The map search we measured their rank on">Search results</Ext>
          {areaUrl ? <Ext href={areaUrl} class="btn btn-sm" title={`Google search: ${areaSearchQuery(area)}`}>Search the area</Ext> : null}
          {!p.readOnly && p.onRecheck ? <button class="btn btn-sm" onClick={blurThen(p.onRecheck)} title="Check again live">Re-check <kbd>R</kbd></button> : null}
          {close}
        </div>
        <div class="vd-bd-sub">
          <span class="muted">{[h.city, h.stateProv].filter(Boolean).join(', ')}{clock ? ` · ${clock} their time ·` : ' ·'}</span>
          <span class={`vd-bd-open vd-bd-${open.tone}`}>{open.text}</span>
          {open.source === 'live' ? <Attr /> : null}
          {rating ? <span>· {rating.text}{rating.live ? ' (live)' : ''}</span> : null}
          {category ? <span class="muted">· {category}</span> : null}
          {checkingLive ? <span class="muted">· checking live…</span> : null}
        </div>
      </div>

      {live?.banner ? (
        <div class="vd-bd-banner" role="alert">
          <b>{live.banner}</b>
          {live.dispositionHint ? <span> If they confirm, log "{DISPOSITION_META[live.dispositionHint]?.label ?? live.dispositionHint}"{DISPOSITION_META[live.dispositionHint]?.key ? ` (${DISPOSITION_META[live.dispositionHint].key})` : ''}.</span> : null}
        </div>
      ) : null}
      {p.error ? <div class="vd-bd-warn">{p.error} Showing the research snapshot.</div> : null}
      {live?.places && !live.places.ok ? <div class="vd-bd-warn">Live Google check failed: {live.places.reason}. Showing the research snapshot.</div> : null}

      <div class="vd-bd-scroll">
        <HookBlock p={p} />

        {!d.researched ? (
          <section class="vd-bd-block vd-bd-research">
            <p style="margin:0">Not researched yet: no map, review or website facts for this lead.</p>
            {researching ? <p class="muted" style="margin:4px 0 0">Research running{d.research.searchId ? ` (search #${d.research.searchId})` : ''}. This panel updates itself.</p> : null}
            {d.research.pendingRequestId ? <p class="muted" style="margin:4px 0 0">Research is waiting for PJ's approval (request #{d.research.pendingRequestId}). You'll get an email when it's decided.</p> : null}
            {!p.readOnly && d.research.available && !researching && !d.research.pendingRequestId && p.onResearch ? (
              <button class="btn btn-sm" style="margin-top:6px" onClick={blurThen(p.onResearch)}>Research this business (about {usdLabel(d.research.estUsd)})</button>
            ) : null}
          </section>
        ) : null}

        <div class="vd-bd-cols">
          <div class="vd-bd-col">
            {d.researched || d.map ? <MapBlock d={d} live={live} now={p.now} /> : null}
            {d.researched || d.phoneMentions.complaints.length || d.phoneMentions.praise ? <MentionsBlock d={d} /> : null}
          </div>
          <div class="vd-bd-col">
            {d.researched || d.website ? <WebsiteBlock d={d} live={live} /> : null}
            <PeopleBlock d={d} />
            {d.businessId !== null ? <HistoryBlock d={d} /> : null}
          </div>
        </div>

        <FooterBlock d={d} live={live} readOnly={p.readOnly} />
        <MoreBlock d={d} />
      </div>
    </aside>
  );
}

function HookBlock(props: { p: DrawerViewProps }) {
  const { p } = props;
  const d = p.data!;
  const hv = hookView(d, p.live);
  const hook = hv.hook;
  const verified = hookVerified(d, hv);
  const gate = verifyGate({
    hook, status: hv.status, evidenceOpened: !!hook && p.evidenceOpened.includes(hook.evidenceUrl), verified, readOnly: p.readOnly, busy: false,
  });
  const complaintMonths = hook?.type === 'phone_complaints' ? d.phoneMentions.complaints.map((m) => fmtMonth(m.month)) : [];
  const statusTone = hv.status === 'still_true' ? 'chip-ok' : 'chip-warn';
  return (
    <section class="vd-bd-hook">
      <div class="vd-bd-row">
        <span class="eyebrow">The hook</span>
        {hook ? <span class={`chip ${hook.usage === 'opener' && hook.openerEligible ? 'chip-ok' : 'chip-info'}`}>{hook.usage === 'opener' && !hook.openerEligible ? 'opener not cleared yet' : USAGE_LABEL[hook.usage]}</span> : null}
        {hook && !p.readOnly && hv.statusText ? <span class={`chip ${statusTone}`}>{hv.statusText}</span> : null}
        <span class="vd-bd-grow" />
        {hook ? <Ext href={hook.evidenceUrl} class="btn btn-sm" title={hook.evidenceUrl} onOpen={() => p.onEvidenceOpened(hook.evidenceUrl)}>Open evidence</Ext> : null}
        {hook && verified ? <span class="chip chip-ok" title="The Script panel can open with it">Verified {fmtShortDate(d.hook.observationVerifiedAt)}</span> : null}
        {gate.show && p.onVerify ? (
          <button class="btn btn-sm btn-ink" disabled={!gate.enabled} title={gate.why ?? 'Confirms it is true. The Script panel can then open with it.'}
            onClick={blurThen(() => { if (gate.enabled && hook) p.onVerify!(hook); })}>Verify</button>
        ) : null}
      </div>
      {hv.withdrawn ? (
        <p class="vd-bd-withdrawn"><s>"{hv.withdrawn.sayText}"</s> <span class="vd-bad">Withdrawn: {hv.withdrawnReason}</span>{hv.isBackupPromoted ? ' The backup is below.' : ''}</p>
      ) : null}
      {hook ? (
        <>
          <p class="vd-bd-say">"{hook.sayText}"</p>
          {complaintMonths.length ? <p class="vd-bd-meta">{complaintMonths.length} complaint{complaintMonths.length === 1 ? '' : 's'}: {complaintMonths.join(', ')}</p> : null}
          {gate.why && !gate.enabled ? <p class="vd-bd-meta">{gate.why}</p> : null}
        </>
      ) : hv.withdrawn ? (
        <p class="vd-bd-say muted">No hook to use now. Use the standard opener.</p>
      ) : (
        <p class="vd-bd-say muted">{d.hook.none ?? 'No verified observation. Use the standard opener.'}</p>
      )}
      <p class="vd-bd-dont"><b>Don't say:</b> {dontSayLine(d)}</p>
      {hv.backup ? (
        <p class="vd-bd-backup">
          <span class="eyebrow">Backup</span> <span class="chip">{USAGE_LABEL[hv.backup.usage]}</span> "{hv.backup.sayText}"{' '}
          <Ext href={hv.backup.evidenceUrl} onOpen={() => p.onEvidenceOpened(hv.backup!.evidenceUrl)}>evidence</Ext>
        </p>
      ) : null}
    </section>
  );
}

function MapBlock(props: { d: DrawerData; live: LiveCheck | null; now: Date }) {
  const { d } = props;
  const m = d.map;
  const hours = hoursLineFor(d, props.live);
  return (
    <section class="vd-bd-block">
      <h4 class="eyebrow">On the map{m?.measuredAt ? ` · research ${fmtShortDate(m.measuredAt)}` : ''}</h4>
      {m ? (
        <ul class="vd-bd-facts">
          <li>{m.reviewsCount !== null ? `${m.reviewsCount} reviews` : 'Review count not measured'}{m.newestReviewAt ? ` · newest ${daysAgo(m.newestReviewAt, props.now)}` : ''}</li>
          <li>{paceLine(m.n12m)} · {replyRateLine(m.ownerReplyRate20, m.reviewsCount)}</li>
          {hours ? <li>{hours.text} {hours.live ? <><span class="muted">(live {fmtClock(hours.measuredAt)})</span> <Attr /></> : <span class="muted">({hours.measuredAt ? fmtShortDate(hours.measuredAt) : 'date unknown'})</span>}</li> : <li class="muted">Hours not measured</li>}
          <li>Claimed: {yesNo(m.claimed)}{m.claimedMeasuredAt ? <span class="muted"> ({fmtShortDate(m.claimedMeasuredAt)})</span> : null}</li>
          {m.ranks.map((r) => <li><Ext href={r.checkUrl} title="The map search this rank came from">{rankLine(r)}</Ext></li>)}
          {!m.ranks.length ? <li class="muted">Map rank not measured</li> : null}
        </ul>
      ) : <p class="muted">Not measured.</p>}
    </section>
  );
}

function MentionsBlock(props: { d: DrawerData }) {
  const { d } = props;
  const list = shownMentions(d);
  return (
    <section class="vd-bd-block">
      <h4 class="eyebrow">Phone mentions (12 months)</h4>
      {list.length ? (
        <ul class="vd-bd-list">
          {list.map((m) => (
            <li class={m.kind === 'praise' ? 'vd-bd-praise' : ''}>
              <span class="vd-bd-meta">{m.kind === 'praise' ? 'Praise · ' : ''}{mentionMeta(m)}</span>{' '}
              <q>{m.quote}</q> <Ext href={m.reviewUrl} title="Open the review">review</Ext>
            </li>
          ))}
        </ul>
      ) : reviewsMeasured(d) ? <p class="muted">None found in the newest reviews we read.</p> : <p class="muted">Reviews not read yet (not measured).</p>}
    </section>
  );
}

function WebsiteBlock(props: { d: DrawerData; live: LiveCheck | null }) {
  const { d } = props;
  const w = d.website;
  const url = w?.url ?? d.header.website;
  const loads = siteLoadsLine(d, props.live);
  return (
    <section class="vd-bd-block">
      <h4 class="eyebrow">Website{url ? ` · ${hostOf(url)}` : ''}</h4>
      {w ? (
        <div class="vd-bd-site">
          {w.screenshotUrl ? (
            <Ext href={w.screenshotUrl} class="vd-bd-shot" title="Their homepage on a phone (PageSpeed screenshot)">
              <img src={w.screenshotUrl} alt={`Homepage of ${hostOf(url)} on a phone`} width={84} loading="eager" decoding="async" />
            </Ext>
          ) : <div class="vd-bd-shot vd-bd-noshot">no screenshot</div>}
          <ul class="vd-bd-facts">
            <li>Loads: <span class={`vd-bd-${loads.tone}`}>{loads.text}</span></li>
            <li>Mobile speed: {w.psiScore !== null ? `${w.psiScore}/100` : 'not measured'}{w.psiMeasuredAt ? <span class="muted"> ({fmtShortDate(w.psiMeasuredAt)})</span> : null}{lcpLine(w.lcpMs) ? ` · ${lcpLine(w.lcpMs)}` : ''}</li>
            <li>Fits phones: {yesNo(w.viewport)} · Tap-to-call link: {yesNo(w.tel)}</li>
            {w.bookingPath ? <li>Booking: {w.bookingPath}</li> : null}
            {w.menuPath || w.menuHost ? <li>Menu: {[w.menuPath, w.menuHost].filter(Boolean).join(' · ')}</li> : null}
            {w.tech.length ? <li>Tech: {w.tech.map((t) => t.vendor).join(', ')}</li> : null}
            {w.builtOn || w.copyrightYear !== null ? <li>{w.builtOn ? `Built on ${w.builtOn}` : ''}{w.builtOn && w.copyrightYear !== null ? ' · ' : ''}{w.copyrightYear !== null ? `© ${w.copyrightYear}` : ''}</li> : null}
            {w.ageCheck !== null ? <li>Age check: {yesNo(w.ageCheck)}</li> : null}
            {w.hasWebVendor ? <li>Has a web vendor</li> : null}
          </ul>
        </div>
      ) : <p class="muted">{url ? 'Website not checked yet.' : 'No website on file.'}</p>}
    </section>
  );
}

function PeopleBlock(props: { d: DrawerData }) {
  const { d } = props;
  return (
    <section class="vd-bd-block">
      <h4 class="eyebrow">People</h4>
      {d.people.length ? (
        <ul class="vd-bd-list">
          {d.people.map((x) => (
            <li>
              <b>{x.name}</b>{x.role ? ` · ${x.role}` : ''} <span class="muted">· {x.label}</span>{' '}
              {x.status === 'unconfirmed' ? <span class="chip chip-warn" title="Not from a licence or their own site: don't use it as the owner's name">unconfirmed</span> : null}{' '}
              <Ext href={x.sourceUrl} title={x.sourceUrl}>source</Ext>
              {x.apolloRef && x.emailAvailable ? <span class="muted"> · Apollo has an email (admin reveal, 1 credit)</span> : null}
            </li>
          ))}
        </ul>
      ) : <p class="muted">No named people found yet.</p>}
      {d.gatekeeperName ? <p class="vd-bd-meta">Gatekeeper (from our calls): <b>{d.gatekeeperName}</b></p> : null}
      {d.emails.length ? (
        <ul class="vd-bd-list">
          {d.emails.map((e) => (
            <li>
              <span class="mono">{e.email}</span>{' '}
              {e.usableForEmail ? <span class="chip chip-ok" title="Verified. Canada: email only an address the owner gives on the call">usable</span> : <span class="chip chip-warn">{e.reason ?? 'not usable for email'}</span>}{' '}
              <span class="muted">{e.verifyStatus.replace(/_/g, ' ')}</span> <Ext href={e.sourceUrl} title={e.sourceUrl}>source</Ext>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function HistoryBlock(props: { d: DrawerData }) {
  const hs = props.d.history;
  const label = (code: keyof typeof DISPOSITION_META) => DISPOSITION_META[code]?.label ?? code;
  const counts = [countLine('Attempts', hs.attempts, hs.maxAttempts), countLine('VMs', hs.vmCount, hs.maxVms)].filter(Boolean);
  return (
    <section class="vd-bd-block">
      <h4 class="eyebrow">Our history</h4>
      {hs.calls.length ? (
        <ul class="vd-bd-list">
          {hs.calls.slice(0, 3).map((c) => <li>{historyLine(c, label)}{c.note ? <div class="vd-bd-meta">{c.note}</div> : null}</li>)}
        </ul>
      ) : <p class="muted">No calls yet.</p>}
      {counts.length ? <p class="vd-bd-meta">{counts.join(' · ')}</p> : null}
      {hs.recycle ? (
        <p class="vd-bd-meta" title={recycleRoundTitle(hs.recycle)}>
          {recycleRoundLabel(hs.recycle)} · {hs.recycle.kind === 'exhausted' ? 'extra round after a rest' : 'back after "has one already"'}
          {hs.recycle.recycledAt ? ` · since ${fmtShortDate(hs.recycle.recycledAt)}` : ''}
        </p>
      ) : null}
      {hs.openCallbacks.map((t) => <p class="vd-bd-meta">Callback due {fmtShortDate(t.dueAt)} {fmtClock(t.dueAt)} · {t.title}</p>)}
      {hs.meetings.map((m) => <p class="vd-bd-meta">Meeting {fmtShortDate(m.scheduledFor)} {fmtClock(m.scheduledFor)} · {m.status.replace(/_/g, ' ')}</p>)}
    </section>
  );
}

function FooterBlock(props: { d: DrawerData; live: LiveCheck | null; readOnly: boolean }) {
  const { d, live } = props;
  const tier = d.header.tier;
  const reasons = reasonsLine(d);
  return (
    <footer class="vd-bd-foot">
      <span><b>{tier ? `Why ${tier}` : 'Score'}{d.offer ? ` (${OFFER_LABEL[d.offer] ?? d.offer})` : ''}:</b> {reasons ?? 'not scored'}</span>
      {d.footer.switchHint ? <span class="chip chip-info">{d.footer.switchHint}</span> : null}
      {!props.readOnly && !d.live.placesAvailable && d.placeId ? <span class="muted">Live Google check off{d.live.placesReason ? `: ${d.live.placesReason}` : ''}</span> : null}
      {!props.readOnly && live ? <span class="muted">Live checks: {live.quota.monthUsed}/{live.quota.monthCap} this month</span> : null}
    </footer>
  );
}

function MoreBlock(props: { d: DrawerData }) {
  const { d } = props;
  const scores = Object.entries(d.footer.scores);
  return (
    <details class="vd-bd-more">
      <summary>More: all phone reviews, competitors, socials, score detail, sources ({d.sources.length})</summary>
      {d.phoneMentions.all.length ? (
        <>
          <h4 class="eyebrow">All phone reviews (12 months)</h4>
          <ul class="vd-bd-list">
            {d.phoneMentions.all.map((m) => <li><span class="vd-bd-meta">{m.kind} · {mentionMeta(m)}</span> <q>{m.quote}</q> <Ext href={m.reviewUrl}>review</Ext></li>)}
          </ul>
        </>
      ) : null}
      {d.competitors.length ? (
        <>
          <h4 class="eyebrow">Competitors nearby (display only; never name them on the call)</h4>
          <ul class="vd-bd-list">
            {d.competitors.map((c) => <li>{c.rank}. {c.name}{c.rating !== null ? ` · ${c.rating.toFixed(1)} stars` : ''}{c.reviewsCount !== null ? ` · ${c.reviewsCount} reviews` : ''}</li>)}
          </ul>
        </>
      ) : null}
      {d.socials.length ? (
        <>
          <h4 class="eyebrow">Business socials (from their site)</h4>
          <p class="vd-bd-meta">{d.socials.map((s) => <><Ext href={s.url} title={s.url}>{s.kind}</Ext>{' '}</>)}</p>
        </>
      ) : null}
      {scores.length ? (
        <>
          <h4 class="eyebrow">Score detail</h4>
          <ul class="vd-bd-list">{scores.map(([offer, s]) => (s ? <li>{OFFER_LABEL[offer] ?? offer}: {scoreLine(s)}</li> : null))}</ul>
          {d.footer.reasons.length ? <ul class="vd-bd-list">{d.footer.reasons.map((r) => <li>{r.points > 0 ? `+${r.points}` : r.points} · {r.text}</li>)}</ul> : null}
        </>
      ) : null}
      {d.sources.length ? (
        <>
          <h4 class="eyebrow">Sources</h4>
          <ul class="vd-bd-list">{d.sources.map((s) => <li><Ext href={s.url} title={s.url}>{s.label}</Ext> <span class="muted">({fmtShortDate(s.measuredAt)})</span></li>)}</ul>
        </>
      ) : null}
    </details>
  );
}
